// Anatomy Explainer Pages Data
// Simple explanations at 8th-10th grade reading level
// All images sourced from Wikimedia Commons - public domain or Creative Commons licensed
// URGENCY LANGUAGE STANDARDIZED per urgencyGuidelines.js
// CONDITIONS NOW INCLUDE CROSS-LINKS to articles and videos

export const anatomyPages = {
  "prostate": {
    id: "prostate",
    title: "The Prostate",
    subtitle: "Understanding this small but important gland",
    images: [
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Diagram_showing_the_position_of_the_prostate_and_rectum_CRUK_358.svg/600px-Diagram_showing_the_position_of_the_prostate_and_rectum_CRUK_358.svg.png",
        alt: "Diagram showing the position of the prostate and rectum",
        caption: "Position of the prostate gland in the male pelvis",
        source: "Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)"
      },
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1f/Prostate_zones.png/600px-Prostate_zones.png",
        alt: "Prostate zones diagram showing different anatomical regions",
        caption: "The different zones of the prostate gland",
        source: "Mikael Häggström, M.D. / Wikimedia Commons (CC0)"
      },
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/c/cd/Benign_Prostatic_Hyperplasia_nci-vol-7137-300.jpg",
        alt: "Comparison of normal prostate and enlarged prostate (BPH)",
        caption: "Normal prostate vs. enlarged prostate (BPH)",
        source: "National Cancer Institute / Wikimedia Commons (Public Domain)"
      }
    ],
    simpleExplanation: "The prostate is a small gland about the size of a walnut. It sits just below the bladder in men. The urethra (the tube that carries urine) runs through the middle of it. The prostate makes fluid that mixes with sperm to create semen.",
    location: "Below the bladder, in front of the rectum",
    size: "About the size of a walnut in young men. It often grows larger with age.",
    function: "Makes fluid that protects and nourishes sperm. This fluid becomes part of semen.",
    commonConditions: [
      {
        name: "BPH (Enlarged Prostate)",
        description: "The prostate grows larger with age. This can squeeze the urethra and make it harder to urinate.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you cannot urinate at all.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you notice blood in urine or significant worsening of symptoms.",
          routine: "Routine follow-up recommended for gradual changes in urinary symptoms."
        },
        links: {
          articles: [
            { title: "What is BPH?", path: "/learn/870b356f-c057-4b37-8404-6423c0dc96bd" },
            { title: "BPH Surgical Treatments", path: "/common-concerns/bph-surgical-treatments" },
            { title: "Urinary Retention (Complication)", path: "/learn/3389df5b-d448-4779-8d46-870a74cf8671" }
          ],
          videos: [
            { title: "Enlarged Prostate (BPH): Symptoms and Treatment", category: "Patient Education" }
          ]
        }
      },
      {
        name: "Prostatitis",
        description: "Inflammation or infection of the prostate. Can cause pain and urination problems.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with chills, severe pelvic pain, or cannot urinate.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have fever ≥100.4°F, painful urination, or pelvic discomfort.",
          routine: "Routine follow-up recommended for mild, chronic symptoms."
        },
        links: {
          articles: [
            { title: "Prostatitis: Understanding Prostate Inflammation", path: "/learn/bc34434d-653f-4d4c-be49-0c655c177d5b" }
          ]
        }
      },
      {
        name: "Prostate Cancer",
        description: "Abnormal cell growth in the prostate. Often grows slowly. Early detection is important.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have severe bone pain, cannot urinate, or have leg weakness/numbness.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have new bone pain, blood in urine, or significant urinary changes.",
          routine: "Routine follow-up recommended for PSA monitoring and surveillance."
        },
        links: {
          articles: [
            { title: "Prostate Cancer Overview", path: "/cancer/prostate-cancer" },
            { title: "Active Surveillance for Prostate Cancer", path: "/learn/86764b75-bfe3-4065-9c7a-23f1c8d5dd25" },
            { title: "Treatment Options", path: "/learn/2e075d98-f708-44bf-816c-fa5de5e88520" }
          ],
          videos: [
            { title: "PSA Testing for Prostate Cancer", category: "Patient Education" }
          ]
        }
      }
    ],
    moreDetail: "The prostate gland is made up of different zones. The transition zone surrounds the urethra and is where BPH develops. The peripheral zone is the outer part where most prostate cancers begin. PSA (Prostate-Specific Antigen) is a protein made by the prostate that can be measured with a blood test."
  },
  
  "bladder": {
    id: "bladder",
    title: "The Bladder",
    subtitle: "Your body's urine storage tank",
    images: [
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/dc/2605_The_Bladder.jpg/600px-2605_The_Bladder.jpg",
        alt: "Detailed anatomy of the urinary bladder showing internal structure",
        caption: "Internal structure of the urinary bladder",
        source: "OpenStax College / Wikimedia Commons (CC BY 3.0)"
      },
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Urinary_system.svg/400px-Urinary_system.svg.png",
        alt: "Complete urinary system showing bladder in context",
        caption: "The bladder within the urinary system",
        source: "Jordi March i Nogué / Wikimedia Commons (CC BY-SA 3.0)"
      }
    ],
    simpleExplanation: "The bladder is a hollow, muscular organ that stores urine. It sits in your lower belly (pelvis). When empty, it is about the size of a pear. It can stretch to hold about 2 cups of urine. When full, muscles in the bladder wall squeeze to push urine out through the urethra.",
    location: "In the lower belly (pelvis), behind the pubic bone",
    size: "About the size of a pear when empty. Can stretch to hold 400-600 mL of urine.",
    function: "Stores urine until you are ready to urinate. Muscles in the wall contract to empty it.",
    commonConditions: [
      {
        name: "Urinary Tract Infection (UTI)",
        description: "Bacteria infect the bladder, causing burning, urgency, and frequent urination.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with chills, severe back/flank pain, confusion, or cannot keep fluids down.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if symptoms don't improve after 48 hours of antibiotics, or if you develop fever ≥100.4°F.",
          routine: "Routine follow-up recommended after completing antibiotics or for recurrent UTI prevention."
        },
        links: {
          articles: [
            { title: "Recurrent UTI Information", path: "/after-visit/recurrent-uti" }
          ],
          videos: [
            { title: "UTI: Symptoms and Treatment", category: "condition" }
          ]
        }
      },
      {
        name: "Overactive Bladder",
        description: "Sudden, strong urges to urinate that are hard to control. May cause leakage.",
        whenToSeekCare: {
          urgent: "Contact a urologist promptly (within 24–72 hours) if you notice blood in urine or cannot urinate.",
          routine: "Routine follow-up recommended for symptom management and treatment adjustment."
        },
        links: {
          articles: [
            { title: "Overactive Bladder: Symptoms and Treatment", path: "/learn/4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6" },
            { title: "Urinary Incontinence Guide", path: "/learn/2c669205-f1b7-4961-966a-22e1db4f72f8" }
          ]
        }
      },
      {
        name: "Bladder Cancer",
        description: "Abnormal cell growth in the bladder lining. Blood in urine is often the first sign.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you cannot urinate due to blood clots, have severe bleeding with dizziness, or have severe pain.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you see blood in your urine (even once) for evaluation.",
          routine: "Routine follow-up recommended for surveillance after treatment."
        },
        links: {
          articles: [
            { title: "Bladder Cancer Overview", path: "/cancer/bladder-cancer" }
          ],
          videos: [
            { title: "TURBT: Bladder Tumor Removal Surgery", category: "Procedure" }
          ]
        }
      },
      {
        name: "Urinary Retention",
        description: "Inability to empty the bladder fully or at all.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you cannot urinate at all and feel bladder fullness/pain.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have increasing difficulty urinating or feel you cannot empty completely.",
          routine: "Routine follow-up recommended for chronic, stable retention being monitored."
        },
        links: {
          articles: [
            { title: "Urinary Retention: Causes, Symptoms & Treatment", path: "/learn/3389df5b-d448-4779-8d46-870a74cf8671" },
            { title: "Catheter Care After Retention", path: "/after-visit/urinary-retention-catheter" },
            { title: "BPH (Common Cause)", path: "/learn/870b356f-c057-4b37-8404-6423c0dc96bd" }
          ]
        }
      }
    ],
    moreDetail: "The bladder wall has three layers of muscle called the detrusor muscle. The bladder neck is where the bladder connects to the urethra. Sphincter muscles at the bladder neck help control when you urinate. The bladder lining (urothelium) is a special type of tissue that can stretch without tearing."
  },
  
  "kidney": {
    id: "kidney",
    title: "The Kidneys",
    subtitle: "Your body's natural filters",
    images: [
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Blausen_0592_KidneyAnatomy_01.png/600px-Blausen_0592_KidneyAnatomy_01.png",
        alt: "Detailed kidney anatomy showing internal structures",
        caption: "Cross-section of the kidney showing internal anatomy",
        source: "Blausen Medical / Wikimedia Commons (CC BY 3.0)"
      },
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Urinary_system.svg/400px-Urinary_system.svg.png",
        alt: "Urinary system diagram showing both kidneys",
        caption: "Both kidneys in the urinary system",
        source: "Jordi March i Nogué / Wikimedia Commons (CC BY-SA 3.0)"
      }
    ],
    simpleExplanation: "You have two kidneys, one on each side of your spine in your back. Each kidney is about the size of your fist. They filter your blood to remove waste and extra water. This waste becomes urine. Your kidneys filter about 200 quarts of blood every day.",
    location: "In the back of your abdomen, on either side of the spine",
    size: "Each kidney is about the size of a fist (4-5 inches long)",
    function: "Filter blood to remove waste and extra fluid. Balance minerals in your body. Help control blood pressure.",
    commonConditions: [
      {
        name: "Kidney Stones",
        description: "Hard deposits of minerals that form in the kidney. Can cause severe pain when passing through the urinary tract.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with flank pain, cannot urinate, have uncontrolled vomiting, or severe pain not relieved by medication.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have fever ≥100.4°F, worsening pain, or persistent vomiting.",
          routine: "Routine follow-up recommended for small stones being monitored or after stone passage."
        },
        links: {
          articles: [
            { title: "Kidney Stones: Causes, Symptoms, and Treatment", path: "/learn/10ca27f4-00b5-4229-a94a-c8a60140a855" },
            { title: "Kidney Stone Conservative Management", path: "/after-visit/kidney-stone-conservative" }
          ],
          videos: [
            { title: "Kidney Stones: Symptoms, Causes & Treatment", category: "condition" },
            { title: "How to Treat Kidney Stones", category: "procedure" }
          ]
        }
      },
      {
        name: "Kidney Infection (Pyelonephritis)",
        description: "A bacterial infection that travels up from the bladder. Causes fever and back pain.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with chills, severe flank pain, vomiting, or confusion.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if UTI symptoms spread to back/flank or fever develops.",
          routine: "Routine follow-up recommended after completing treatment to ensure infection cleared."
        },
        links: {
          articles: [
            { title: "Recurrent UTI Information", path: "/after-visit/recurrent-uti" }
          ]
        }
      },
      {
        name: "Hydronephrosis",
        description: "Swelling of the kidney when urine cannot drain properly.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever with known obstruction, severe pain, or signs of infection.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have new flank pain or known hydronephrosis with worsening symptoms.",
          routine: "Routine follow-up recommended for stable, monitored hydronephrosis."
        },
        links: {
          articles: [
            { title: "Hydronephrosis: When the Kidney Swells", path: "/learn/d7e6d442-cdee-4b79-a91c-40b103cb883a" }
          ]
        }
      },
      {
        name: "Kidney Cancer",
        description: "Abnormal cell growth in the kidney. Often found during tests for other conditions.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have severe bleeding with dizziness, severe pain, or cannot urinate.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you see blood in urine, have new flank pain, or unexplained weight loss.",
          routine: "Routine follow-up recommended for surveillance of small renal masses."
        },
        links: {
          articles: [
            { title: "Kidney Cancer Overview", path: "/cancer/kidney-cancer" },
            { title: "Small Renal Masses", path: "/articles/small-renal-mass" }
          ],
          videos: [
            { title: "The Kidney and Kidney Cancers", category: "Condition" },
            { title: "Kidney Cancer: Diagnosis, Risks and Treatment", category: "Condition" }
          ]
        }
      }
    ],
    moreDetail: "Each kidney contains about one million tiny filtering units called nephrons. The renal pelvis is the funnel-shaped area where urine collects before flowing into the ureter. The kidneys also make hormones that help control blood pressure and tell your body to make red blood cells."
  },
  
  "ureter": {
    id: "ureter",
    title: "The Ureters",
    subtitle: "The tubes connecting kidneys to bladder",
    images: [
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Urinary_system.svg/400px-Urinary_system.svg.png",
        alt: "Urinary system diagram showing both ureters connecting kidneys to bladder",
        caption: "The ureters (labeled 4) connect the kidneys to the bladder",
        source: "Jordi March i Nogué / Wikimedia Commons (CC BY-SA 3.0)"
      }
    ],
    simpleExplanation: "The ureters are two thin tubes, one from each kidney. They carry urine from your kidneys down to your bladder. Each ureter is about 10-12 inches long. Muscles in the ureter walls squeeze to push urine along, like squeezing toothpaste from a tube.",
    location: "Running from each kidney down to the bladder",
    size: "About 10-12 inches long and about as wide as a pencil",
    function: "Carry urine from the kidneys to the bladder using wave-like muscle contractions.",
    commonConditions: [
      {
        name: "Ureteral Stones",
        description: "Kidney stones that have moved into the ureter. Can block urine flow and cause severe pain.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with flank pain (sign of infected/obstructed kidney), cannot urinate, or have uncontrolled pain.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have fever ≥100.4°F, worsening pain, or persistent nausea/vomiting.",
          routine: "Routine follow-up recommended for small stones expected to pass or post-procedure checks."
        },
        links: {
          articles: [
            { title: "Kidney Stones Guide", path: "/learn/10ca27f4-00b5-4229-a94a-c8a60140a855" },
            { title: "Ureteral Stent Care", path: "/after-visit/ureteral-stent-care" }
          ],
          videos: [
            { title: "Ureteroscopy for Kidney Stones", category: "procedure" }
          ]
        }
      },
      {
        name: "Ureteral Stricture",
        description: "Narrowing of the ureter that blocks or slows urine flow.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever with known stricture, severe flank pain, or signs of infection.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have new or worsening flank pain.",
          routine: "Routine follow-up recommended for stable strictures being monitored."
        },
        links: {
          articles: [
            { title: "Hydronephrosis (Related)", path: "/learn/d7e6d442-cdee-4b79-a91c-40b103cb883a" }
          ]
        }
      },
      {
        name: "Vesicoureteral Reflux",
        description: "When urine flows backward from the bladder into the ureters.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with back pain (possible kidney infection).",
          urgent: "Contact a urologist promptly (within 24–72 hours) if UTI symptoms develop.",
          routine: "Routine follow-up recommended for surveillance and management planning."
        }
      }
    ],
    moreDetail: "The ureters enter the bladder at an angle, which helps prevent urine from flowing backward. The narrowest parts of the ureter are where it leaves the kidney, where it crosses blood vessels, and where it enters the bladder. Kidney stones often get stuck at these narrow points."
  },
  
  "urethra": {
    id: "urethra",
    title: "The Urethra",
    subtitle: "The tube that carries urine out",
    images: [
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/d/d7/Gray1142.png",
        alt: "Gray's Anatomy illustration of the male urethra",
        caption: "Anatomical view of the male urethra (Gray's Anatomy)",
        source: "Henry Vandyke Carter / Gray's Anatomy (Public Domain)"
      },
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Diagram_showing_the_position_of_the_prostate_and_rectum_CRUK_358.svg/600px-Diagram_showing_the_position_of_the_prostate_and_rectum_CRUK_358.svg.png",
        alt: "Diagram showing urethra passing through the prostate",
        caption: "The urethra passes through the prostate gland",
        source: "Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)"
      }
    ],
    simpleExplanation: "The urethra is the tube that carries urine from your bladder out of your body. In men, it also carries semen during ejaculation. The male urethra is about 8 inches long and passes through the prostate and penis. The female urethra is much shorter, about 1.5 inches.",
    location: "Runs from the bladder to outside the body",
    size: "Male: about 8 inches. Female: about 1.5 inches.",
    function: "Carries urine out of the body. In men, also carries semen during ejaculation.",
    commonConditions: [
      {
        name: "Urethritis",
        description: "Inflammation of the urethra, often from infection. Causes burning during urination.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you cannot urinate at all or have high fever with severe symptoms.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have discharge, persistent burning, or fever ≥100.4°F.",
          routine: "Routine follow-up recommended after treatment to confirm resolution."
        },
        links: {
          articles: [
            { title: "Urethritis Overview", path: "/common-concerns/urethritis" }
          ]
        }
      },
      {
        name: "Urethral Stricture",
        description: "Narrowing of the urethra that makes urination difficult or slow.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you cannot urinate at all.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have significant worsening of stream, new inability to empty bladder, or signs of UTI.",
          routine: "Routine follow-up recommended for stable symptoms and treatment planning."
        },
        links: {
          articles: [
            { title: "Urethral Stricture: Causes and Treatment", path: "/learn/04b53673-d9c2-4421-93dc-5c4ef8ffbec0" }
          ]
        }
      },
      {
        name: "Urinary Tract Infection",
        description: "Infection that can affect the urethra, especially in women due to shorter length.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have fever ≥101°F with chills, severe back pain, confusion, or cannot keep fluids down.",
          urgent: "Contact a urologist promptly (within 24–72 hours) if symptoms persist beyond 48 hours on antibiotics or fever develops.",
          routine: "Routine follow-up recommended for recurrent UTI evaluation."
        },
        links: {
          articles: [
            { title: "Recurrent UTI Information", path: "/after-visit/recurrent-uti" }
          ],
          videos: [
            { title: "UTI: Symptoms and Treatment", category: "condition" }
          ]
        }
      }
    ],
    moreDetail: "In men, the urethra has three sections: the prostatic urethra (through the prostate), the membranous urethra (through the pelvic floor), and the penile urethra (through the penis). Sphincter muscles around the urethra help control urination."
  },
  
  "testicle": {
    id: "testicle",
    title: "The Testicles",
    subtitle: "Male reproductive glands",
    images: [
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Diagram_of_the_testicles_CRUK_048.svg/600px-Diagram_of_the_testicles_CRUK_048.svg.png",
        alt: "Diagram of the testicles showing anatomical structure",
        caption: "Anatomy of the testicles",
        source: "Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)"
      },
      {
        url: "https://upload.wikimedia.org/wikipedia/commons/5/55/Figure_28_01_03.JPG",
        alt: "Detailed testicle anatomy showing internal structures",
        caption: "Internal structure of the testicle and epididymis",
        source: "OpenStax College / Wikimedia Commons (CC BY 3.0)"
      }
    ],
    simpleExplanation: "The testicles (also called testes) are two oval-shaped glands in the scrotum. They make sperm and the hormone testosterone. The scrotum holds the testicles outside the body to keep them slightly cooler than body temperature, which is needed for sperm production.",
    location: "In the scrotum, the skin pouch below the penis",
    size: "Each testicle is about the size of a small egg (about 2 inches long)",
    function: "Make sperm for reproduction. Produce testosterone, the main male hormone.",
    commonConditions: [
      {
        name: "Testicular Torsion",
        description: "When the testicle twists, cutting off blood supply. A surgical emergency.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately for sudden severe testicular pain, testicle appearing higher or rotated, or testicular pain with nausea/vomiting. The testicle can die within 6 hours without treatment.",
          urgent: null,
          routine: null
        },
        links: {
          articles: [
            { title: "Emergency Urology Guide", path: "/common-concerns/emergency-urology" }
          ]
        }
      },
      {
        name: "Epididymitis",
        description: "Inflammation of the epididymis (the tube behind the testicle). Causes pain and swelling.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if you have sudden severe pain (to rule out torsion), fever ≥101°F with severe swelling, or scrotal skin changes (redness spreading).",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have gradual testicular pain, swelling, or fever ≥100.4°F.",
          routine: "Routine follow-up recommended after treatment to confirm resolution."
        },
        links: {
          articles: [
            { title: "Epididymitis Explained", path: "/common-concerns/epididymitis" },
            { title: "Testicular Pain Information", path: "/after-visit/testicular-pain" }
          ]
        }
      },
      {
        name: "Varicocele",
        description: "Enlarged veins in the scrotum. Usually painless but can affect fertility.",
        whenToSeekCare: {
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have new pain or sudden increase in size.",
          routine: "Routine follow-up recommended for evaluation, especially if concerned about fertility."
        },
        links: {
          articles: [
            { title: "Varicoceles – Common Questions", path: "/common-concerns/varicoceles" }
          ]
        }
      },
      {
        name: "Testicular Cancer",
        description: "Abnormal cell growth in the testicle. Most common in young men. Very treatable when found early.",
        whenToSeekCare: {
          urgent: "Contact a urologist promptly (within 24–72 hours) if you notice a new lump, painless swelling, heaviness in the scrotum, or change in testicle size/shape.",
          routine: "Routine follow-up recommended for surveillance after treatment."
        },
        links: {
          articles: [
            { title: "Testicular Cancer Overview", path: "/cancer/testicular-cancer" }
          ],
          videos: [
            { title: "How to Check Your Testicles: Self-Exam Guide", category: "General Education" },
            { title: "Testicular Cancer: What You Really Need to Know", category: "Condition" }
          ]
        }
      },
      {
        name: "Hydrocele",
        description: "Fluid buildup around the testicle causing swelling. Usually painless.",
        whenToSeekCare: {
          emergency: "Seek emergency care immediately if sudden painful swelling (to rule out torsion or infection).",
          urgent: "Contact a urologist promptly (within 24–72 hours) if you have new pain, redness, or rapid size increase.",
          routine: "Routine follow-up recommended for gradual, painless swelling for diagnosis."
        },
        links: {
          articles: [
            { title: "Hydrocele: Fluid Around the Testicle", path: "/learn/e8d1762f-03d3-4bb0-afac-5472fd81d3d7" },
            { title: "Scrotal Cysts Explained", path: "/common-concerns/scrotal-cysts" }
          ]
        }
      }
    ],
    moreDetail: "The epididymis is a coiled tube behind each testicle where sperm mature and are stored. The vas deferens is the tube that carries sperm from the epididymis to the urethra during ejaculation. A vasectomy cuts or blocks the vas deferens for permanent birth control."
  }
};

export const getAnatomyPage = (id) => {
  return anatomyPages[id] || null;
};

export const getAllAnatomyPages = () => {
  return Object.values(anatomyPages);
};
